

// Privacy contract (ported with the schema): the assistant may read database
// SCHEMA but must NEVER receive row data — db_run_sql returns only execution
// status, row count and result column names/types; rows render in the grid.
export const DB_TOOLS = [
  {
    name: 'db_list_connections',
    description:
      'List DB Manager connections (saved and currently live): conn_id, name, engine, database, live. The host, the account it logs in as, a SQLite file path and every credential stay in the vault and are never returned — you act by conn_id, and the user sees the real target on the confirmation card. Call first to pick a conn_id for db_get_schema / db_run_sql.',
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'db_get_schema',
    description:
      'Get the schema for a database connection: tables and views with their columns (name, type, nullable, primary key). Returns NO row data. Always call this before writing SQL so you use real table/column names and types.',
    input_schema: {
      type: 'object',
      properties: {
        conn_id: { type: 'string' },
        database: {
          type: 'string',
          description:
            'Optional database/schema name (MySQL/Postgres). Omit for SQLite or the connection default.',
        },
      },
      required: ['conn_id'],
    },
  },
  {
    name: 'db_run_sql',
    description:
      'Open a new SQL tab in DB Manager with the given query and run it. The results appear in the DB Manager grid — you do NOT receive the rows. You only get back { executed, row_count, columns:[{name}] }; on failure, the database error with quoted values replaced by <value>. Use db_get_schema first to build a correct query. Interactive mode switches to DB Manager and runs there.',
    input_schema: {
      type: 'object',
      properties: {
        conn_id: { type: 'string' },
        database: { type: 'string', description: 'Optional database/schema context' },
        sql: { type: 'string' },
      },
      required: ['conn_id', 'sql'],
    },
  },
];

export const DOCS_TOOLS = [
  {
    name: 'get_documents',
    description:
      "List the Documents tree: folders and documents (id, title, parent_id, scope, workspace). scope is 'personal', 'personal/<workspaceId>' (one of the user's own workspaces, named in workspace) or 'team:<id>'. Call to locate a document by title or to choose where to create a new one.",
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'read_document',
    description:
      "Read a document's title and body text by id. Images appear as \"🖼 Image: name\" lines. Use to gather context before writing/summarizing documentation.",
    input_schema: {
      type: 'object',
      properties: { document_id: { type: 'string' } },
      required: ['document_id'],
    },
  },
  {
    name: 'create_doc_folder',
    description:
      "Create a folder in the Documents tree. Optional parent_id nests it under another folder. Without parent_id, optional scope ('personal' or 'personal/<workspaceId>' from get_documents) picks the workspace. Returns the folder id.",
    input_schema: {
      type: 'object',
      properties: {
        parent_id: { type: 'string' },
        name: { type: 'string' },
        scope: { type: 'string' },
      },
      required: ['name'],
    },
  },
  {
    name: 'create_document',
    description:
      "Create a new document with a title and optional initial content (markdown preferred). Optional parent_id places it in a folder; without one, optional scope ('personal' or 'personal/<workspaceId>' from get_documents) picks the workspace. Always switches to the Documents panel and opens the new document. Returns document_id.",
    input_schema: {
      type: 'object',
      properties: {
        parent_id: { type: 'string' },
        scope: { type: 'string' },
        title: { type: 'string' },
        content: { type: 'string' },
        format: { type: 'string', enum: ['markdown', 'html'] },
      },
      required: ['title'],
    },
  },
  {
    name: 'open_document',
    description:
      "Open an existing document in the Documents panel (switches to it). Use when the user says 'open', 'show', or 'go to' a document. Provide document_id (use get_documents first to resolve a title to an id).",
    input_schema: {
      type: 'object',
      properties: { document_id: { type: 'string' } },
      required: ['document_id'],
    },
  },
  {
    name: 'rename_document',
    description: 'Rename an existing document. Provide document_id and the new title.',
    input_schema: {
      type: 'object',
      properties: {
        document_id: { type: 'string' },
        title: { type: 'string' },
      },
      required: ['document_id', 'title'],
    },
  },
  {
    name: 'delete_document',
    description:
      'Delete a document permanently. Provide document_id. Destructive — only call when the user clearly asks to delete/remove a document.',
    input_schema: {
      type: 'object',
      properties: { document_id: { type: 'string' } },
      required: ['document_id'],
    },
  },
  {
    name: 'write_document',
    description:
      "Write content into an existing document. mode 'replace' overwrites the body; mode 'append' adds to the end. Content is markdown by default. Use for 'write documentation for…', 'add a section', etc.",
    input_schema: {
      type: 'object',
      properties: {
        document_id: { type: 'string' },
        content: { type: 'string' },
        mode: { type: 'string', enum: ['replace', 'append'] },
        format: { type: 'string', enum: ['markdown', 'html'] },
      },
      required: ['document_id', 'content'],
    },
  },
];

// Privacy contract (ported with the schema): only vault connection IDS and
// command OUTPUT ever reach the model — SSH credentials are decrypted inside
// the Rust core and there is no tool that returns them.
export const TERMINAL_TOOLS = [
  {
    name: 'terminal_list_connections',
    description:
      "List the SSH-capable server connections (from the user's vault): conn_id and name only. Call first to pick a conn_id for terminal_run_command. The host, the account it logs in as, and every credential stay in the vault and are never returned — you act by conn_id, and the user sees the real target on the confirmation card.",
    input_schema: { type: 'object', properties: {} },
  },
  {
    name: 'terminal_run_command',
    description:
      'Run one shell command on the server behind an SSH connection and get { stdout, stderr, exit_code } back (output capped at 256 KB per stream). The session auto-connects on first use and is reused. Non-interactive exec: no PTY, no prompts — commands requiring interactive input (sudo password, editors) will hang until the timeout and should be avoided. Prefer small, targeted commands and read the output before the next step.',
    input_schema: {
      type: 'object',
      properties: {
        conn_id: { type: 'string' },
        command: { type: 'string' },
        timeout_secs: {
          type: 'integer',
          description: 'Max seconds to wait (default 30, max 300).',
        },
      },
      required: ['conn_id', 'command'],
    },
  },
];
